// Premium top-down court render: zone colours, layered game lines, tile texture,
// hoops, centre logo text and lighting. VersaCourt-class visuals, Speedy branding.
const OVERLAY_FALLBACKS = ["#F2F2F2", "#2E6FB0", "#141414", "#E4761B", "#5B3B8C"];

const Basketball = ({ x, y, w, h, c, keyColor, half }) => {
  const keyW = w * 0.19, keyH = h * 0.38, cy = y + h / 2;
  return (
    <g stroke={c} strokeWidth="2.5" fill="none">
      <rect x={x} y={y} width={w} height={h} />
      <rect x={x} y={cy - keyH / 2} width={keyW} height={keyH} fill={keyColor} />
      <path d={`M${x + keyW} ${cy - keyH / 2} A${keyH / 2} ${keyH / 2} 0 0 1 ${x + keyW} ${cy + keyH / 2}`} />
      <path d={`M${x} ${y + h * 0.12} A${w * 0.46} ${w * 0.46} 0 0 1 ${x} ${y + h * 0.88}`} />
      {!half && (
        <>
          <rect x={x + w - keyW} y={cy - keyH / 2} width={keyW} height={keyH} fill={keyColor} />
          <path d={`M${x + w - keyW} ${cy - keyH / 2} A${keyH / 2} ${keyH / 2} 0 0 0 ${x + w - keyW} ${cy + keyH / 2}`} />
          <path d={`M${x + w} ${y + h * 0.12} A${w * 0.46} ${w * 0.46} 0 0 0 ${x + w} ${y + h * 0.88}`} />
          <line x1={x + w / 2} y1={y} x2={x + w / 2} y2={y + h} />
          <circle cx={x + w / 2} cy={cy} r={h * 0.16} />
        </>
      )}
      {half && <line x1={x + w} y1={y} x2={x + w} y2={y + h} strokeWidth="3.5" />}
    </g>
  );
};

const Netball = ({ x, y, w, h, c, half }) => {
  const cy = y + h / 2, r = h * 0.3;
  return (
    <g stroke={c} strokeWidth="2" fill="none">
      <rect x={x} y={y} width={w} height={h} />
      <path d={`M${x} ${cy - r} A${r} ${r} 0 0 1 ${x} ${cy + r}`} />
      {!half && (
        <>
          <path d={`M${x + w} ${cy - r} A${r} ${r} 0 0 0 ${x + w} ${cy + r}`} />
          <line x1={x + w / 3} y1={y} x2={x + w / 3} y2={y + h} />
          <line x1={x + (2 * w) / 3} y1={y} x2={x + (2 * w) / 3} y2={y + h} />
          <circle cx={x + w / 2} cy={cy} r={h * 0.08} />
        </>
      )}
    </g>
  );
};

const Tennis = ({ x, y, w, h, c, half }) => {
  const inX = x + w * 0.06, inW = w * 0.88, inY = y + h * 0.14, inH = h * 0.72;
  const svcX1 = inX + inW * 0.25, svcX2 = inX + inW * 0.75, cy = inY + inH / 2;
  if (half) {
    return (
      <g stroke={c} strokeWidth="2" fill="none">
        <rect x={x + w * 0.1} y={y + h * 0.12} width={w * 0.8} height={h * 0.76} />
        <line x1={x + w * 0.1} y1={cy} x2={x + w * 0.9} y2={cy} strokeDasharray="4 4" />
      </g>
    );
  }
  return (
    <g stroke={c} strokeWidth="2" fill="none">
      <rect x={inX} y={inY} width={inW} height={inH} />
      <line x1={inX} y1={inY + inH * 0.14} x2={inX + inW} y2={inY + inH * 0.14} />
      <line x1={inX} y1={inY + inH * 0.86} x2={inX + inW} y2={inY + inH * 0.86} />
      <line x1={svcX1} y1={inY + inH * 0.14} x2={svcX1} y2={inY + inH * 0.86} />
      <line x1={svcX2} y1={inY + inH * 0.14} x2={svcX2} y2={inY + inH * 0.86} />
      <line x1={svcX1} y1={cy} x2={svcX2} y2={cy} />
      <line x1={inX + inW / 2} y1={inY - 4} x2={inX + inW / 2} y2={inY + inH + 4} strokeWidth="3" />
    </g>
  );
};

const Pickleball = ({ x, y, w, h, c, half }) => {
  const inX = x + w * (half ? 0.12 : 0.2), inW = w * (half ? 0.76 : 0.6), inY = y + h * 0.2, inH = h * 0.6;
  const midX = inX + inW / 2, kW = inW * 0.16;
  return (
    <g stroke={c} strokeWidth="2" fill="none">
      <rect x={inX} y={inY} width={inW} height={inH} />
      <line x1={midX} y1={inY - 3} x2={midX} y2={inY + inH + 3} strokeWidth="3" />
      <line x1={midX - kW} y1={inY} x2={midX - kW} y2={inY + inH} />
      <line x1={midX + kW} y1={inY} x2={midX + kW} y2={inY + inH} />
      <line x1={inX} y1={inY + inH / 2} x2={midX - kW} y2={inY + inH / 2} />
      <line x1={midX + kW} y1={inY + inH / 2} x2={inX + inW} y2={inY + inH / 2} />
    </g>
  );
};

const Futsal = ({ x, y, w, h, c, half }) => {
  const cy = y + h / 2, boxH = h * 0.52, boxW = w * 0.14;
  return (
    <g stroke={c} strokeWidth="2" fill="none">
      <rect x={x} y={y} width={w} height={h} />
      <rect x={x} y={cy - boxH / 2} width={boxW} height={boxH} />
      <path d={`M${x + boxW} ${cy - 14} A16 16 0 0 1 ${x + boxW} ${cy + 14}`} />
      {!half && (
        <>
          <rect x={x + w - boxW} y={cy - boxH / 2} width={boxW} height={boxH} />
          <path d={`M${x + w - boxW} ${cy - 14} A16 16 0 0 0 ${x + w - boxW} ${cy + 14}`} />
          <line x1={x + w / 2} y1={y} x2={x + w / 2} y2={y + h} />
          <circle cx={x + w / 2} cy={cy} r={h * 0.14} />
        </>
      )}
      {half && <line x1={x + w} y1={y} x2={x + w} y2={y + h} strokeWidth="3.5" />}
    </g>
  );
};

const Hoop = ({ x, cy, flip }) => {
  const dir = flip ? -1 : 1;
  return (
    <g stroke="#E8E8E8" strokeWidth="2" fill="none" opacity="0.9">
      <line x1={x} y1={cy - 11} x2={x} y2={cy + 11} strokeWidth="3" />
      <circle cx={x + 7 * dir} cy={cy} r="4.5" stroke="#FF8A3C" />
    </g>
  );
};

const SPORT_RENDER = { Basketball, Netball, Tennis, Pickleball, Futsal };

// Real court dimensions in metres (length × width)
export const SIZE_DIMS = {
  full: { length: 28, width: 15 },
  half: { length: 14, width: 15 },
  threes: { length: 15, width: 11 },
  junior: { length: 10, width: 7 },
};

export default function DesignerPreview({ zones, sports = ["Basketball"], size = "full", dims, tiles = false, logoText = "", logoColor = "#FFD84A", showDims = true, className = "" }) {
  const d = (size === "custom" ? dims : SIZE_DIMS[size]) || dims || SIZE_DIMS.full;
  const courtL = Math.min(Math.max(Number(d.length) || 28, 4), 60);
  const courtW = Math.min(Math.max(Number(d.width) || 15, 3), 40);
  const half = size === "custom" ? courtL < 20 : size !== "full";
  // True-to-scale: px per metre so every size renders at its real aspect ratio
  const scale = Math.min(440 / courtL, 240 / courtW);
  const pad = 50, bPad = 34;
  const w = courtL * scale, h = courtW * scale;
  const W = w + pad * 2, H = h + pad * 2;
  const x = pad, y = pad;
  const cx = half ? x + w * 0.62 : x + w / 2, cy = y + h / 2;
  const hasBball = sports.includes("Basketball");
  const fmt = (n) => (Number.isInteger(n) ? n : n.toFixed(1));
  let overlayIdx = 0;
  return (
    <div className={className} data-testid="designer-preview">
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto rounded-md ring-1 ring-white/10 shadow-[0_40px_90px_rgba(0,0,0,0.85)]">
        <defs>
          <pattern id="tileGrid" width="11" height="11" patternUnits="userSpaceOnUse">
            <path d="M11 0H0V11" fill="none" stroke="rgba(0,0,0,0.16)" strokeWidth="0.8" />
          </pattern>
          <radialGradient id="courtLight" cx="50%" cy="42%" r="75%">
            <stop offset="0%" stopColor="rgba(255,255,255,0.10)" />
            <stop offset="60%" stopColor="rgba(255,255,255,0)" />
            <stop offset="100%" stopColor="rgba(0,0,0,0.28)" />
          </radialGradient>
        </defs>
        {/* surround */}
        <rect x="0" y="0" width={W} height={H} fill="#151618" />
        <rect x="0" y="0" width={W} height={H} fill="url(#tileGrid)" opacity="0.25" />
        {/* dimension markers */}
        {showDims && (
          <g data-testid="dimension-markers">
            <g stroke="rgba(255,255,255,0.35)" strokeWidth="1">
              <line x1={x} y1={y - 24} x2={x + w} y2={y - 24} />
              <line x1={x} y1={y - 29} x2={x} y2={y - 19} />
              <line x1={x + w} y1={y - 29} x2={x + w} y2={y - 19} />
              <line x1={x - 24} y1={y} x2={x - 24} y2={y + h} />
              <line x1={x - 29} y1={y} x2={x - 19} y2={y} />
              <line x1={x - 29} y1={y + h} x2={x - 19} y2={y + h} />
            </g>
            <text x={x + w / 2} y={y - 32} textAnchor="middle" fontFamily="ui-monospace, monospace" fontSize="11" letterSpacing="1.5" fill="rgba(255,216,74,0.85)">{`${fmt(courtL)}m`}</text>
            <text x={x - 32} y={y + h / 2} textAnchor="middle" fontFamily="ui-monospace, monospace" fontSize="11" letterSpacing="1.5" fill="rgba(255,216,74,0.85)" transform={`rotate(-90 ${x - 32} ${y + h / 2})`}>{`${fmt(courtW)}m`}</text>
          </g>
        )}
        {/* border / apron */}
        <rect x={bPad} y={bPad} width={W - bPad * 2} height={H - bPad * 2} rx="3" fill={zones.border} />
        {tiles && <rect x={bPad} y={bPad} width={W - bPad * 2} height={H - bPad * 2} rx="3" fill="url(#tileGrid)" />}
        {/* main surface */}
        <rect x={x} y={y} width={w} height={h} fill={zones.base} />
        {tiles && <rect x={x} y={y} width={w} height={h} fill="url(#tileGrid)" />}
        {/* centre circle zone */}
        {!half && <circle cx={cx} cy={cy} r={h * 0.16} fill={zones.centre} />}
        {/* game lines */}
        {sports.map((s, i) => {
          const Render = SPORT_RENDER[s];
          if (!Render) return null;
          const colour = i === 0 ? zones.lines : OVERLAY_FALLBACKS[overlayIdx++ % OVERLAY_FALLBACKS.length];
          return <Render key={s} x={x} y={y} w={w} h={h} c={colour} keyColor={i === 0 ? zones.key : "none"} half={half} />;
        })}
        {/* hoops */}
        {hasBball && <Hoop x={x + 6} cy={cy} />}
        {hasBball && !half && <Hoop x={x + w - 6} cy={cy} flip />}
        {/* centre logo */}
        {logoText && !half && (
          <text x={cx} y={cy + 4} textAnchor="middle" fontFamily="Arial Black, Arial, sans-serif" fontWeight="900"
            fontSize={Math.min(14, 120 / Math.max(logoText.length, 4))} letterSpacing="1.5" fill={logoColor} opacity="0.92">
            {logoText.toUpperCase()}
          </text>
        )}
        {logoText && half && (
          <text x={x + w * 0.55} y={y + h * 0.16} textAnchor="middle" fontFamily="Arial Black, Arial, sans-serif" fontWeight="900"
            fontSize="10" letterSpacing="1.5" fill={logoColor} opacity="0.92">{logoText.toUpperCase()}</text>
        )}
        {/* lighting + vignette */}
        <rect x="0" y="0" width={W} height={H} fill="url(#courtLight)" pointerEvents="none" />
      </svg>
    </div>
  );
}
